import api from './api';
import { submitAndPoll } from './aiJobService';

// ==================== Types ====================
export type BlogConnectionType = 'wordpress' | 'rest_api' | 'webhook' | 'devskin_marketplace';
export type BlogAuthType = 'app_password' | 'bearer' | 'api_key' | 'basic' | 'none';
export type BlogPostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'archived';
export type BlogPostKind = 'article' | 'news' | 'tutorial' | 'listicle' | 'review';

export interface BlogConnection {
  id: string;
  name: string;
  type: BlogConnectionType;
  site_url: string;
  api_endpoint?: string | null;
  auth_type: BlogAuthType;
  username?: string | null;
  api_key_header?: string | null;
  extra_headers?: Record<string, string>;
  default_status: string;
  status: 'connected' | 'error' | 'disconnected';
  last_checked_at?: string | null;
  last_error?: string | null;
  capabilities?: any;
  is_default: boolean;
  has_credential?: boolean;
  flux_enabled: boolean;
  flux_app_id?: string | null;
  flux_site_id?: string | null;
  flux_api_key_masked?: string;
  // devskin_marketplace: loja e endereço do blog (vêm da chave)
  metadata?: { store_id?: string; store_name?: string; public_url?: string | null; blog_url?: string | null; category_id?: string; [k: string]: any };
  created_at?: string;
}

export interface SeoMeta {
  metaTitle?: string;
  metaDescription?: string;
  focusKeyword?: string;
  keywords?: string[];
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  checks?: SeoCheck[];
}

export interface SeoCheck {
  id: string;
  label: string;
  status: 'good' | 'warning' | 'bad';
  weight: number;
  hint: string;
}

export interface SeoAnalysis {
  score: number;
  checks: SeoCheck[];
  wordCount: number;
  keywordDensity: number;
}

export interface BlogPost {
  id: string;
  connection_id?: string | null;
  connection?: { id: string; name: string; type: string; site_url: string };
  title: string;
  slug?: string;
  content: string;
  excerpt?: string;
  kind: BlogPostKind;
  status: BlogPostStatus;
  scheduled_at?: string | null;
  published_at?: string | null;
  external_url?: string | null;
  featured_image_url?: string | null;
  categories?: string[];
  tags?: string[];
  seo?: SeoMeta;
  seo_score: number;
  word_count: number;
  source: string;
  error_message?: string | null;
  metadata?: any;
  created_at?: string;
}

export interface ContentSchedule {
  id: string;
  name: string;
  channels: string[];
  blog_connection_id?: string | null;
  blogConnection?: { id: string; name: string };
  social_platforms: string[];
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  days_of_week: number[];
  time_of_day: string;
  timezone: string;
  posts_per_run: number;
  topics: string[];
  tone?: string | null;
  auto_publish: boolean;
  status: 'active' | 'paused';
  last_run_at?: string | null;
  next_run_at?: string | null;
  runs_count: number;
  metadata?: { ignore_pattern?: boolean; variation_direction?: string; kind?: string };
  // sent on create/update (stored server-side in metadata)
  ignore_pattern?: boolean;
  variation_direction?: string;
  kind?: string;
}

export interface GenerateRequest {
  channels: string[];               // ['blog']
  kind?: string;                    // article | news | tutorial | listicle | review
  count: number;
  topics?: string[];
  tone?: string;
  connection_id?: string | null;
  social_platforms?: string[];
  schedule?: { mode: 'draft' | 'now' | 'schedule'; dates?: string[]; autoPublish?: boolean };
  ignore_pattern?: boolean;         // "fugir do padrão dos posts anteriores"
  variation_direction?: string;     // "direção desta variação"
}

// ===== SEO Studio (live URL analysis + competitor comparison) =====
export interface SeoPageAnalysis {
  url: string;
  statusCode?: number;
  loadTime?: number;
  contentLength?: number;
  score: number;
  meta?: {
    title?: string; titleLength?: number;
    description?: string; descriptionLength?: number;
    keywords?: string; canonical?: string;
    ogTitle?: string; ogDescription?: string; ogImage?: string;
  };
  headings?: { h1?: string[]; h2?: string[]; h3?: string[]; h1Count?: number; h2Count?: number; h3Count?: number };
  links?: { internalCount?: number; externalCount?: number };
  images?: { total?: number; withAlt?: number; withoutAlt?: number };
  content?: { wordCount?: number; paragraphs?: number; textToHtmlRatio?: number };
  technical?: { hasViewport?: boolean; hasCharset?: boolean; hasLangAttribute?: boolean; usesHttps?: boolean; hasStructuredData?: boolean; hasFavicon?: boolean };
  issues?: Array<{ type: 'error' | 'warning' | 'info'; code?: string; message: string }>;
}

export interface SeoRecommendation {
  title: string; why: string; how: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
  category: string;
}
export interface SeoActionPlan {
  summary: string;
  score: number;
  recommendations: SeoRecommendation[];
}
export interface SeoComparison {
  site: { url: string; analysis: SeoPageAnalysis };
  competitors: Array<{ url: string; analysis: SeoPageAnalysis }>;
  aiComparison: { verdict: string; youWin: string[]; youLose: string[]; opportunities: string[]; quickWins: string[] } | null;
}

export interface SeoAnalysisRow {
  id: string;
  type: 'site' | 'compare';
  url: string;
  competitors?: string[];
  status: 'running' | 'completed' | 'failed';
  score?: number | null;
  // For type 'site': { analysis, aiPlan }. For type 'compare': SeoComparison.
  result?: ({ analysis?: SeoPageAnalysis; aiPlan?: SeoActionPlan | null } & Partial<SeoComparison>) | null;
  error_message?: string | null;
  created_at?: string;
  completed_at?: string | null;
}

export interface AnalyticsOverview {
  posts: { total: number; published: number; scheduled: number; drafts: number };
  avgSeoScore: number;
  connections: number;
  flux: {
    enabled: boolean;
    appId?: string | null;
    installSnippet: string;
    sdkUrl: string;
    apiUrl: string;
    dashboardUrl: string;
    hint: string;
  };
}

// ==================== Service ====================
export const blogService = {
  // Connections
  async listConnections(): Promise<{ success: boolean; data: BlogConnection[] }> {
    return (await api.get('/blog/connections')).data;
  },
  async createConnection(data: Partial<BlogConnection> & { credential?: string; flux_api_key?: string; marketplace_connection_id?: string }): Promise<any> {
    return (await api.post('/blog/connections', data)).data;
  },
  async updateConnection(id: string, data: Partial<BlogConnection> & { credential?: string; flux_api_key?: string; marketplace_connection_id?: string }): Promise<any> {
    return (await api.put(`/blog/connections/${id}`, data)).data;
  },
  async deleteConnection(id: string): Promise<any> {
    return (await api.delete(`/blog/connections/${id}`)).data;
  },
  async testConnection(payload: { id?: string } & Partial<BlogConnection> & { credential?: string; marketplace_connection_id?: string }): Promise<any> {
    if (payload.id) return (await api.post(`/blog/connections/${payload.id}/test`)).data;
    return (await api.post('/blog/connections/test', payload)).data;
  },
  async getCategories(id: string): Promise<{ success: boolean; data: { id: number | string; name: string; count: number }[] }> {
    return (await api.get(`/blog/connections/${id}/categories`)).data;
  },

  // Posts
  async listPosts(params?: { status?: string; kind?: string; search?: string; page?: number; limit?: number }): Promise<any> {
    return (await api.get('/blog/posts', { params })).data;
  },
  async getPost(id: string): Promise<{ success: boolean; data: BlogPost }> {
    return (await api.get(`/blog/posts/${id}`)).data;
  },
  async createPost(data: Partial<BlogPost>): Promise<{ success: boolean; data: BlogPost }> {
    return (await api.post('/blog/posts', data)).data;
  },
  async bulkCreatePosts(posts: Partial<BlogPost>[]): Promise<any> {
    return (await api.post('/blog/posts/bulk', { posts })).data;
  },
  async updatePost(id: string, data: Partial<BlogPost>): Promise<{ success: boolean; data: BlogPost }> {
    return (await api.put(`/blog/posts/${id}`, data)).data;
  },
  async deletePost(id: string): Promise<any> {
    return (await api.delete(`/blog/posts/${id}`)).data;
  },
  async publishPost(id: string, connection_id?: string): Promise<any> {
    return (await api.post(`/blog/posts/${id}/publish`, { connection_id })).data;
  },
  async regeneratePost(id: string, tone?: string): Promise<{ success: boolean; data: BlogPost }> {
    const data = await submitAndPoll<BlogPost>(
      () => api.post(`/blog/posts/${id}/regenerate`, { tone }).then((r) => r.data), undefined, 3000, 180000
    );
    return { success: true, data };
  },

  // Image upload (web) — same S3 endpoint the rest of the system uses
  async uploadImage(file: File): Promise<{ success: boolean; url: string; key?: string }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  },

  // SEO on-page score (live)
  async analyzeSeo(payload: Partial<BlogPost>): Promise<{ success: boolean; data: SeoAnalysis }> {
    return (await api.post('/blog/seo/analyze', payload)).data;
  },

  // SEO analyses are PERSISTED (run in background, survive navigation, build history).
  // seoSite/seoCompare create a row (status 'running'); poll getSeoAnalysis until completed.
  async seoSite(url: string): Promise<{ success: boolean; data: SeoAnalysisRow }> {
    return (await api.post('/blog/seo/site', { url })).data;
  },
  async seoCompare(url: string, competitors: string[]): Promise<{ success: boolean; data: SeoAnalysisRow }> {
    return (await api.post('/blog/seo/compare', { url, competitors })).data;
  },
  async listSeoAnalyses(type?: 'site' | 'compare'): Promise<{ success: boolean; data: SeoAnalysisRow[] }> {
    return (await api.get('/blog/seo/analyses', { params: type ? { type } : {} })).data;
  },
  async getSeoAnalysis(id: string): Promise<{ success: boolean; data: SeoAnalysisRow }> {
    return (await api.get(`/blog/seo/analyses/${id}`)).data;
  },
  async deleteSeoAnalysis(id: string): Promise<any> {
    return (await api.delete(`/blog/seo/analyses/${id}`)).data;
  },

  // AI: write a full article from a topic/title (returns draft fields, not saved)
  async aiWrite(payload: { topic?: string; title?: string; kind?: string; tone?: string }): Promise<{
    success: boolean;
    data: { title: string; slug: string; excerpt: string; content: string; categories: string[]; tags: string[]; seo: SeoMeta; seoScore: number; wordCount: number; kind: string };
  }> {
    const data = await submitAndPoll<{ title: string; slug: string; excerpt: string; content: string; categories: string[]; tags: string[]; seo: SeoMeta; seoScore: number; wordCount: number; kind: string }>(
      () => api.post('/blog/ai/write', payload).then((r) => r.data), undefined, 3000, 180000
    );
    return { success: true, data };
  },

  // Content engine
  async generate(req: GenerateRequest): Promise<{ success: boolean; data: { blogPosts: BlogPost[]; socialPosts: any[]; topics: string[]; blogCount: number; socialCount: number } }> {
    const data = await submitAndPoll<{ blogPosts: BlogPost[]; socialPosts: any[]; topics: string[]; blogCount: number; socialCount: number }>(
      () => api.post('/blog/generate', req).then((r) => r.data), undefined, 3000, 300000
    );
    return { success: true, data };
  },

  // Recurring schedules
  async listSchedules(): Promise<{ success: boolean; data: ContentSchedule[] }> {
    return (await api.get('/blog/schedules')).data;
  },
  async createSchedule(data: Partial<ContentSchedule>): Promise<any> {
    return (await api.post('/blog/schedules', data)).data;
  },
  async updateSchedule(id: string, data: Partial<ContentSchedule>): Promise<any> {
    return (await api.put(`/blog/schedules/${id}`, data)).data;
  },
  async deleteSchedule(id: string): Promise<any> {
    return (await api.delete(`/blog/schedules/${id}`)).data;
  },
  async runScheduleNow(id: string): Promise<any> {
    return (await api.post(`/blog/schedules/${id}/run`)).data;
  },

  // Analytics
  async getAnalytics(): Promise<{ success: boolean; data: AnalyticsOverview }> {
    return (await api.get('/blog/analytics/overview')).data;
  }
};

export default blogService;
